import ELK from 'elkjs/lib/elk.bundled.js';
import { Edge, MarkerType, Node, Position } from 'reactflow';
import { capitalizeString } from 'src/utils/strings';
import {
  isValidEdgeShape,
  isValidNodeShape,
  isWorkflowDefinitionShape,
  isWorkflowNodeType,
  ParsedEdgeShape,
  ParsedNodeShape,
  ParsedWorkflowDefinition,
  WorkflowDefinition,
  WorkflowDefinitionEdge,
  WorkflowDefinitionNode,
  WorkflowNodeType,
} from './workflow-definition';

export interface WorkflowNodeDisplayData {
  title: string;
  nodeType: WorkflowNodeType;
  nodeId: string;
  rawData?: Record<string, unknown>;
  description?: string;
  icon?: string;
  iconColor?: string;
  headerColor?: string;
  accentColor?: string;
  detailsLabel?: string;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}

interface ReactFlowGraph {
  nodes: Node<WorkflowNodeDisplayData>[];
  edges: Edge[];
}

const COLUMN_COUNT = 3;
const COLUMN_WIDTH = 320;
const ROW_HEIGHT = 180;
const ELK_NODE_WIDTH = 280;
const ELK_NODE_HEIGHT = 160;

export enum WorkflowLayoutDirection {
  Down = 'DOWN',
  Right = 'RIGHT',
}

const elk = new ELK();
const getElkOptions = (direction: WorkflowLayoutDirection) => ({
  'elk.algorithm': 'layered',
  'elk.direction': direction,
  'elk.layered.spacing.nodeNodeBetweenLayers': '150',
  'elk.spacing.nodeNode': '100',
  'elk.layered.spacing.edgeNodeBetweenLayers': '40',
  'elk.layered.crossingMinimization.strategy': 'LAYER_SWEEP',
  'elk.edgeRouting': 'ORTHOGONAL',
});

const formatNodeTitle = (type: WorkflowNodeType) => {
  switch (type) {
    case WorkflowNodeType.CreateConversationForPhoneCall:
      return 'Create Conversation';
    case WorkflowNodeType.SendToFlexUI:
      return 'Send to Flex UI';
    case WorkflowNodeType.ConnectCallTo:
      return 'Connect Call';
    case WorkflowNodeType.SetVariables:
      return 'Set Variables';
    case WorkflowNodeType.Trigger:
      return 'Trigger';
    default:
      return capitalizeString(type);
  }
};

interface NodeVisualConfig {
  icon: string;
  iconColor?: string;
  headerColor: string;
  accentColor: string;
  detailsLabel?: string;
}

interface NodeVisualThemeConfig {
  headerColor: string;
  accentColor: string;
}

interface NodeVisualConfigEntry {
  icon: string;
  iconColor?: string;
  detailsLabel?: string;
  light: NodeVisualThemeConfig;
  dark: NodeVisualThemeConfig;
}

const DEFAULT_VISUAL_CONFIG: NodeVisualConfigEntry = {
  icon: 'category',
  detailsLabel: 'Expand details',
  light: {
    headerColor: '#EDF1F6',
    accentColor: '#F7F9FC',
  },
  dark: {
    headerColor: '#3B4A5A',
    accentColor: '#2B3847',
  },
};

const NODE_VISUAL_CONFIG: Partial<Record<WorkflowNodeType, NodeVisualConfigEntry>> = {
  [WorkflowNodeType.Trigger]: {
    icon: 'play_circle',
    light: {
      headerColor: '#E1F5FE',
      accentColor: '#F0FBFF',
    },
    dark: {
      headerColor: '#1B4F5A',
      accentColor: '#0F3C47',
    },
  },
  [WorkflowNodeType.If]: {
    icon: 'schema',
    light: {
      headerColor: '#F0E8FF',
      accentColor: '#F9F5FF',
    },
    dark: {
      headerColor: '#3D2E5A',
      accentColor: '#2B1E42',
    },
  },
  [WorkflowNodeType.Say]: {
    icon: 'campaign',
    light: {
      headerColor: '#E6ECFF',
      accentColor: '#EFF2FF',
    },
    dark: {
      headerColor: '#2C3A5A',
      accentColor: '#1F2A42',
    },
  },
  [WorkflowNodeType.CreateConversationForPhoneCall]: {
    icon: 'call',
    light: {
      headerColor: '#E8F5E9',
      accentColor: '#F4FBF5',
    },
    dark: {
      headerColor: '#1E4620',
      accentColor: '#153016',
    },
  },
  [WorkflowNodeType.Play]: {
    icon: 'play_arrow',
    light: {
      headerColor: '#FFF3E0',
      accentColor: '#FFF8E7',
    },
    dark: {
      headerColor: '#4A3F1F',
      accentColor: '#362B14',
    },
  },
  [WorkflowNodeType.SetVariables]: {
    icon: 'dataset',
    light: {
      headerColor: '#EDE7F6',
      accentColor: '#F5F2FB',
    },
    dark: {
      headerColor: '#3B3350',
      accentColor: '#2A2438',
    },
  },
  [WorkflowNodeType.SendToFlexUI]: {
    icon: 'support_agent',
    light: {
      headerColor: '#E1F5FE',
      accentColor: '#F0FBFF',
    },
    dark: {
      headerColor: '#1B4F5A',
      accentColor: '#0F3C47',
    },
  },
  [WorkflowNodeType.ConnectCallTo]: {
    icon: 'phone_forwarded',
    light: {
      headerColor: '#FFF8E1',
      accentColor: '#FFFCEB',
    },
    dark: {
      headerColor: '#4A4520',
      accentColor: '#362F14',
    },
  },
};

const getNodeVisualConfig = (type: WorkflowNodeType, isDarkMode = false): NodeVisualConfig => {
  const visualConfig = NODE_VISUAL_CONFIG[type] ?? DEFAULT_VISUAL_CONFIG;
  const { light, dark, ...sharedConfig } = visualConfig;
  const themeConfig = isDarkMode ? dark : light;

  return {
    ...sharedConfig,
    ...themeConfig,
  };
};

// It applies automatic layout to the given nodes and edges using ELK
const applyElkLayout = async (
  nodes: Node<WorkflowNodeDisplayData>[],
  edges: Edge[],
  direction: WorkflowLayoutDirection,
): Promise<Node<WorkflowNodeDisplayData>[]> => {
  if (!nodes.length) {
    return nodes;
  }

  const graph = {
    id: 'workflow-root',
    layoutOptions: getElkOptions(direction),
    children: nodes.map((node) => ({
      id: node.id,
      width: ELK_NODE_WIDTH,
      height: ELK_NODE_HEIGHT,
    })),
    edges: edges.map((edge) => ({
      id: edge.id,
      sources: [edge.source],
      targets: [edge.target],
    })),
  };

  const layoutedGraph = await elk.layout(graph);
  const nodePositions = new Map(
    layoutedGraph.children?.map((child) => [child.id, child]) ?? [],
  );

  return nodes.map((node) => {
    const layoutNode = nodePositions.get(node.id);
    if (!layoutNode) {
      return node;
    }

    return {
      ...node,
      position: {
        x: layoutNode.x ?? node.position.x ?? 0,
        y: layoutNode.y ?? node.position.y ?? 0,
      },
      targetPosition: Position.Top,
      sourcePosition: Position.Bottom,
    };
  });
};

export const buildReactFlowGraph = async (
  definition: WorkflowDefinition,
  isDarkMode = false,
  layoutDirection: WorkflowLayoutDirection = WorkflowLayoutDirection.Down,
): Promise<ReactFlowGraph> => {
  const nodes: Node<WorkflowNodeDisplayData>[] = definition.nodes.map((node, index) => {
    const visualConfig = getNodeVisualConfig(node.type, isDarkMode);
    const position =
      node.position ??
      ({
        x: (index % COLUMN_COUNT) * COLUMN_WIDTH,
        y: Math.floor(index / COLUMN_COUNT) * ROW_HEIGHT,
      } as const);

    return {
      id: node.id,
      type: 'workflowNode',
      data: {
        title: formatNodeTitle(node.type),
        nodeType: node.type,
        nodeId: node.id,
        rawData: node.data ?? {},
        icon: visualConfig.icon,
        iconColor: visualConfig.iconColor,
        headerColor: visualConfig.headerColor,
        accentColor: visualConfig.accentColor,
        detailsLabel: visualConfig.detailsLabel,
      },
      position,
      targetPosition: Position.Top,
      sourcePosition: Position.Bottom,
      draggable: true,
      zIndex: index + 1,
    };
  });

  const edges: Edge[] = definition.edges.map((edge) => {
    const isConditional = typeof edge.data?.condition === 'boolean';
    let sourceHandle: string | undefined;
    if (isConditional) {
      sourceHandle = edge.data?.condition ? 'if-true' : 'if-false';
    }

    const isFalseCondition = isConditional && !edge.data?.condition;

    let conditionSuffix = '';
    if (isConditional) {
      conditionSuffix = edge.data?.condition ? '-true' : '-false';
    }

    return {
      id: `${edge.id}${conditionSuffix}`,
      source: edge.source,
      target: edge.target,
      type: 'smoothstep',
      sourceHandle,
      animated: false,
      markerEnd: {
        type: MarkerType.ArrowClosed,
        width: 0,
        height: 0,
        color: 'transparent',
      },
      style: {
        stroke: isFalseCondition ? '#d32f2f' : '#00c853',
        strokeDasharray: isFalseCondition ? '5 5' : undefined,
        strokeWidth: 2,
      },
      labelBgPadding: [4, 2],
      labelBgBorderRadius: 4,
      labelBgStyle: { fill: isDarkMode ? '#2B3847' : '#fff' },
    };
  });

  const layoutedNodes = await applyElkLayout(nodes, edges, layoutDirection).catch((error) => {
    console.warn(
      'Failed to layout workflow graph with ELK. Falling back to default layout.',
      error,
    );
    return nodes;
  });

  return { nodes: layoutedNodes, edges };
};
export const parseWorkflowDefinition = (
  rawDefinition?: string | null,
): ParsedWorkflowDefinition => {
  if (!rawDefinition) {
    return { definition: null, error: null };
  }

  try {
    const parsed = JSON.parse(rawDefinition) as unknown;
    if (!isWorkflowDefinitionShape(parsed)) {
      throw new Error('Definition must include nodes and edges arrays.');
    }

    const nodes: WorkflowDefinitionNode[] = parsed.nodes
      .filter(isValidNodeShape)
      .map((node: ParsedNodeShape) => {
        const normalizedType = isWorkflowNodeType(node.type)
          ? node.type
          : WorkflowNodeType.Trigger;

        return {
          id: node.id,
          type: normalizedType,
          data: node.data ?? {},
          position:
            typeof node.position?.x === 'number' && typeof node.position?.y === 'number'
              ? node.position
              : undefined,
        };
      });

    const edges: WorkflowDefinitionEdge[] = parsed.edges
      .filter(isValidEdgeShape)
      .map((edge: ParsedEdgeShape, index: number) => ({
        id: edge.id ?? `edge-${index}`,
        source: edge.source,
        target: edge.target,
        data: edge.data,
      }));

    if (!nodes.length) {
      throw new Error('Definition does not contain any nodes.');
    }

    return { definition: { nodes, edges }, error: null };
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Unable to parse definition.';
    return { definition: null, error: message };
  }
};
